import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { api } from './api';
import { ToastProvider, useToast } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import { appUrl, isCustomTrustHost } from './utils/urls';
import { useHostnameGuard } from './utils/useHostnameGuard';
import './index.css';

const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const SsoComplete = lazy(() => import('./pages/SsoComplete'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const Schedules = lazy(() => import('./pages/Schedules'));
const Reports = lazy(() => import('./pages/Reports'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));
import { NoIndex, NoIndexEnRutaInexistente } from './seo/NoIndex';
const ScanProgress = lazy(() => import('./pages/ScanProgress'));
const FindingsList = lazy(() => import('./pages/FindingsList'));
const GlobalFindings = lazy(() => import('./pages/GlobalFindings'));
const FindingDetail = lazy(() => import('./pages/FindingDetail'));
const RetestView = lazy(() => import('./pages/RetestView'));
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard'));
const PciReport = lazy(() => import('./pages/PciReport'));
const BenchmarkView = lazy(() => import('./pages/BenchmarkView'));
const ProjectSettings = lazy(() => import('./pages/ProjectSettings'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const Billing = lazy(() => import('./pages/Billing'));
const StorageSettings = lazy(() => import('./pages/StorageSettings'));
const SessionsSettings = lazy(() => import('./pages/SessionsSettings'));
const SharedReport = lazy(() => import('./pages/SharedReport'));
const TeamSettings = lazy(() => import('./pages/TeamSettings'));
const MyInvites = lazy(() => import('./pages/MyInvites'));
const McpSettings = lazy(() => import('./pages/McpSettings'));
const FreeTools = lazy(() => import('./pages/FreeTools'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const GithubScan = lazy(() => import('./pages/GithubScan'));
const TrustCenter = lazy(() => import('./pages/TrustCenter'));
const TrustAccess = lazy(() => import('./pages/TrustAccess'));
const TrustCenterSettings = lazy(() => import('./pages/TrustCenterSettings'));

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (!api.isAuthenticated()) {
    // If we are on a non-app origin (e.g. condorsecurity.co), jump to the
    // app subdomain's login with a full-page load. Otherwise do a regular
    // SPA redirect.
    const target = appUrl('/login');
    if (!target.startsWith('/')) {
      if (typeof window !== 'undefined') window.location.replace(target);
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Enforces the landing-vs-app hostname split (SSR-safe, no-op in dev). */
function HostnameGuard() {
  useHostnameGuard();
  return null;
}

/** Wires api auto-logout to navigate to /login and show a toast. */
function AutoLogoutWatcher() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  useEffect(() => {
    api.onAutoLogout((reason) => {
      queryClient.cancelQueries();
      queryClient.clear();
      localStorage.removeItem('condor_user_name');
      localStorage.removeItem('condor_role');
      // Only surface the "Session expired" toast when the logout was
      // triggered by a 401 (user's session actually expired). Explicit
      // user-initiated logouts (clicking the Logout button) shouldn't
      // read as an error.
      if (reason === 'auto') {
        showToast('Session expired', 'error');
      }
      const target = appUrl('/login');
      if (target.startsWith('/')) {
        navigate(target, { replace: true });
      } else if (typeof window !== 'undefined') {
        window.location.replace(target);
      }
    });
  }, [navigate, showToast]);
  return null;
}

/** Backfills condor_role in localStorage for already-authenticated sessions
 * that pre-date the role-sync fix. One-shot fetch of /me on mount. */
function RoleBackfill() {
  useEffect(() => {
    if (!api.isAuthenticated()) return;
    if (localStorage.getItem('condor_role')) return;
    api.getProfile()
      .then((p) => {
        if (p.platform_role) localStorage.setItem('condor_role', p.platform_role);
      })
      .catch(() => { /* best-effort; admin nav just stays hidden */ });
  }, []);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
      <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

export default function App() {
  // Customer trust-center domains (CNAME → trust.condorsecurity.co) serve ONLY
  // the public trust center — no login, landing or app routes.
  if (isCustomTrustHost()) {
    return (
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Tokenized access-grant view on customer domains, ahead of the catch-all. */}
                  <Route path="/access/:token" element={<TrustAccess byDomain />} />
                  <Route path="*" element={<TrustCenter byDomain />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </ToastProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <AutoLogoutWatcher />
            <RoleBackfill />
            <HostnameGuard />
            <NoIndex />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/sso/complete" element={<SsoComplete />} />
                {/* El callback OAuth de GitHub deja la misma cookie de sesión que el SSO:
                    la misma página la adopta. */}
                <Route path="/auth/github/complete" element={<SsoComplete />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* 🔴 La ruta a la que apunta el enlace del correo (condor-api la
                    arma en auth.py como `{app_base_url}/reset-password?token=…`).
                    Sin esta línea el enlace caía en el catch-all y mostraba un 404:
                    nadie que llegara por el correo podía cambiar su contraseña.
                    Es la MISMA página — lee el token del query param y arranca en
                    el paso de contraseña nueva. */}
                <Route path="/reset-password" element={<ForgotPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/findings"
                  element={
                    <ProtectedRoute>
                      <GlobalFindings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/schedules"
                  element={
                    <ProtectedRoute>
                      <Schedules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <ProtectedRoute>
                      <Projects />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:id"
                  element={
                    <ProtectedRoute>
                      <ProjectDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/scans/:scanId"
                  element={
                    <ProtectedRoute>
                      <ScanProgress />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/scans/:scanId/findings"
                  element={
                    <ProtectedRoute>
                      <FindingsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/scans/:scanId/findings/:findingId"
                  element={
                    <ProtectedRoute>
                      <FindingDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/scans/:scanId/retest"
                  element={
                    <ProtectedRoute>
                      <RetestView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/scans/:scanId/pci"
                  element={
                    <ProtectedRoute>
                      <PciReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/executive"
                  element={
                    <ProtectedRoute>
                      <ExecutiveDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/benchmark"
                  element={
                    <ProtectedRoute>
                      <BenchmarkView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/settings"
                  element={
                    <ProtectedRoute>
                      <ProjectSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/me/invites"
                  element={
                    <ProtectedRoute>
                      <MyInvites />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/audit-log"
                  element={
                    <ProtectedRoute>
                      <AuditLog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/billing"
                  element={
                    <ProtectedRoute>
                      <Billing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/storage"
                  element={
                    <ProtectedRoute>
                      <StorageSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/team"
                  element={
                    <ProtectedRoute>
                      <TeamSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/sessions"
                  element={
                    <ProtectedRoute>
                      <SessionsSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/mcp"
                  element={
                    <ProtectedRoute>
                      <McpSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPanel />
                    </ProtectedRoute>
                  }
                />
                <Route path="/free-tools" element={<FreeTools />} />
                <Route
                  path="/integrations/github"
                  element={
                    <ProtectedRoute>
                      <GithubScan />
                    </ProtectedRoute>
                  }
                />
                <Route path="/reports/shared/:token" element={<SharedReport />} />
                <Route path="/trust/:slug" element={<TrustCenter />} />
                <Route path="/trust/:slug/access/:token" element={<TrustAccess />} />
                <Route
                  path="/settings/trust-center"
                  element={
                    <ProtectedRoute>
                      <TrustCenterSettings />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NoIndexEnRutaInexistente><NotFound /></NoIndexEnRutaInexistente>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ErrorBoundary>
      </ToastProvider>
    </QueryClientProvider>
  );
}
